import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Stack } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { URLPaths } from 'models/enums';
import SuccessDialog from 'domains/core/components/SuccessDialog';
import PageLayout from 'domains/core/components/PageLayout';
import useGetUserById from 'hooks/queries/useGetUserById';
import useGetRolesList from 'hooks/queries/useGetRolesList';
import useAssignUserRole from 'hooks/mutations/useAssignUserRole';
import UserInformationCard from './UserInformationCard';
import SelectNewRoleCard from './SelectNewRoleCard';

const PAGE_HEADER = 'Change User Role';
const CHANGE_ROLE_BUTTON_LABEL = 'Change Role';
const SUCCESS_DIALOG_TITLE = 'Role updated';
const SUCCESS_DIALOG_BODY = "The user's role has been updated successfully.";
const BACK_TO_USERS_BUTTON_LABEL = 'Back to Users list';

const contentStackStyles = {
    spacing: 3,
};

export type UserRoleDetailsPageProps = {
    /** Optional userId override (e.g. for tests). When not provided, uses :userId from route. */
    userId?: string;
};

const UserRoleDetailsPage = ({ userId: userIdProp }: UserRoleDetailsPageProps = {}) => {
    const history = useHistory();
    const { userId: userIdFromParams } = useParams<{ userId: string }>();
    const userId = userIdProp ?? userIdFromParams ?? '';
    const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const { data: userData } = useGetUserById(userId, {
        enabled: Boolean(userId),
    });
    const { data: rolesData } = useGetRolesList({
        limit: 100,
        offset: 0,
        reactQueryOptions: { enabled: Boolean(userId) },
    });
    const assignRoleMutation = useAssignUserRole(userId, {
        onSuccess: () => setIsSuccessDialogOpen(true),
    });

    const user = userData?.user;
    const roles = rolesData?.data ?? [];

    const effectiveSelectedRoleId = selectedRoleId ?? user?.role_id ?? null;
    const handleChangeRole = () => {
        if (effectiveSelectedRoleId == null) return;
        assignRoleMutation.mutate({ roleId: effectiveSelectedRoleId });
    };

    const headerAction = (
        <Button
            variant="contained"
            onClick={handleChangeRole}
            disabled={assignRoleMutation.isLoading}
            data-testid="access-center-change-role-button"
        >
            {CHANGE_ROLE_BUTTON_LABEL}
        </Button>
    );

    const handleBackToUsersList = () => {
        history.push(URLPaths.SETTINGS_USERS);
    };

    return (
        <Stack data-testid="access-center-user-role-details-page" role="main" aria-label={PAGE_HEADER}>
            <PageLayout header={PAGE_HEADER} headerIcon={<Settings fontSize="inherit" />} headerAction={headerAction}>
                <Stack spacing={contentStackStyles.spacing}>
                    <UserInformationCard user={user} />
                    <SelectNewRoleCard
                        roles={roles}
                        currentRoleId={user?.role_id}
                        selectedRoleId={effectiveSelectedRoleId}
                        onSelectRole={setSelectedRoleId}
                    />
                </Stack>
            </PageLayout>
            <SuccessDialog
                title={SUCCESS_DIALOG_TITLE}
                body={SUCCESS_DIALOG_BODY}
                isOpen={isSuccessDialogOpen}
                setIsOpen={setIsSuccessDialogOpen}
                onClose={() => setIsSuccessDialogOpen(false)}
                buttonOneText="Close"
                buttonTwoText={BACK_TO_USERS_BUTTON_LABEL}
                buttonOneOnClick={() => {}}
                buttonTwoOnClick={handleBackToUsersList}
            />
        </Stack>
    );
};

export default UserRoleDetailsPage;
